/* tslint:disable */
/* eslint-disable */

import * as runtime from '../runtime';
import { BaseAPI } from '../baseApi';
import {
  CreateShopItemPayload,
  EditShopItemPayload,
  Purchase,
  ShopItem,
  WishlistEntry,
} from '../types';
import { HttpStatusCode } from 'axios';
import { ResponseError } from '../runtime';

/**
 * ShopApi — block 3: Stjärnshop (SHOP-01..06).
 *
 * LIVE mot backend (ShopController/ShopHandler). Se BACKEND-KONTRAKT-NPF.md, block 3.
 * Stjärnavdrag vid köp och återbetalning vid avvisat köp sker ATOMISKT i backend mot
 * StarBalance (StarGoalApi.getStarBalance). Frontend gör aldrig lokal saldoberäkning.
 *
 * Roller: vara-CRUD och inlösen/avvisning = förälder (via supervisedTag). Köp/önskelista = barn.
 */

// --- Förälder: varuhantering (SHOP-01) ---
export type ListShopItemsRequest = {
  supervisedTag?: string;
  category?: ShopItem['category'];
};

export type CreateShopItemRequest = {
  payload: CreateShopItemPayload;
  supervisedTag?: string;
};

export type EditShopItemRequest = {
  shopItemId: string;
  payload: EditShopItemPayload;
  supervisedTag?: string;
};

export type RemoveShopItemRequest = {
  shopItemId: string;
  supervisedTag?: string;
};

// --- Barn: köp (SHOP-03) ---
export type CreatePurchaseRequest = {
  shopItemId: string;
  supervisedTag?: string;
};

// --- Förälder: inlösen / avvisning (SHOP-04) ---
export type FulfillPurchaseRequest = {
  purchaseId: string;
  supervisedTag?: string;
};

export type RejectPurchaseRequest = {
  purchaseId: string;
  rejectionNote: string | null;
  supervisedTag?: string;
};

// --- Köphistorik (SHOP-06) ---
export type ListPurchasesRequest = {
  supervisedTag?: string;
  status?: Purchase['status'];
};

// --- Önskelista (SHOP-05) ---
export type AddWishlistRequest = {
  shopItemId: string;
  supervisedTag?: string;
};

export type RemoveWishlistRequest = {
  shopItemId: string;
  supervisedTag?: string;
};

export type ListWishlistRequest = {
  supervisedTag?: string;
};

export class ShopApi extends BaseAPI {
  // GET /shop/items?supervisedTag=&category=
  listShopItems = async (
    requestParameters: ListShopItemsRequest,
  ): Promise<ShopItem[] | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.category !== undefined) {
      queryParameters['category'] = requestParameters.category;
    }
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/items`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): ShopItem[] => jsonValue,
      ).value();
    } catch (error) {}
  };

  // POST /shop/items  (förälder)
  createShopItem = async (
    requestParameters: CreateShopItemRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/items`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.payload),
      });
      return response.status;
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  };

  // PUT /shop/items/{shopItemId}  (förälder)
  editShopItem = async (
    requestParameters: EditShopItemRequest,
  ): Promise<HttpStatusCode | ResponseError | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/items/${requestParameters.shopItemId}`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody(requestParameters.payload),
      });
      return response.status;
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  };

  // DELETE /shop/items/{shopItemId}  (förälder; pågående köp påverkas ej)
  removeShopItem = async (
    requestParameters: RemoveShopItemRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/items/${requestParameters.shopItemId}`,
        method: 'DELETE',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };

  /**
   * POST /shop/purchases  (barn köper; atomiskt stjärnavdrag, 409 vid otillräckligt saldo)
   *
   * Backend svarar 201 med HELA Purchase-objektet — det är barnets kvitto och
   * returneras därför istället för bara statuskoden. Anropare skiljer på utfall via
   * `instanceof ResponseError` (HTTP-fel med `error`-kod i body) respektive
   * `undefined` (nätverksfel/oparsbart svar).
   */
  createPurchase = async (
    requestParameters: CreatePurchaseRequest,
  ): Promise<Purchase | ResponseError | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/purchases`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody({ shopItemId: requestParameters.shopItemId }),
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Purchase => jsonValue,
      ).value();
    } catch (error: unknown) {
      if (error instanceof ResponseError) return error;
    }
  };

  /**
   * GET /shop/purchases?supervisedTag=&status=  (köphistorik, skrivskyddad)
   *
   * UTAN supervisedTag: den inloggade användarens EGNA köp (barnvyn "Mina köp"),
   * alla statusar, nyast först. MED supervisedTag: förälderns vy över barnets köp.
   */
  listPurchases = async (
    requestParameters: ListPurchasesRequest,
  ): Promise<Purchase[] | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.status !== undefined) {
      queryParameters['status'] = requestParameters.status;
    }
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/purchases`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): Purchase[] => jsonValue,
      ).value();
    } catch (error) {}
  };

  // PUT /shop/purchases/{purchaseId}/fulfill  (förälder: "Vi har köpt det!")
  fulfillPurchase = async (
    requestParameters: FulfillPurchaseRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/purchases/${requestParameters.purchaseId}/fulfill`,
        method: 'PUT',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };

  // PUT /shop/purchases/{purchaseId}/reject  (förälder avvisar; atomisk återbetalning)
  rejectPurchase = async (
    requestParameters: RejectPurchaseRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/purchases/${requestParameters.purchaseId}/reject`,
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody({ rejectionNote: requestParameters.rejectionNote }),
      });
      return response.status;
    } catch (error) {}
  };

  // GET /shop/wishlist?supervisedTag=  (barnets önskelista)
  listWishlist = async (
    requestParameters: ListWishlistRequest,
  ): Promise<WishlistEntry[] | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/wishlist`,
        method: 'GET',
        query: queryParameters,
      });

      return await new runtime.JSONApiResponse(
        response,
        (jsonValue): WishlistEntry[] => jsonValue,
      ).value();
    } catch (error) {}
  };

  // POST /shop/wishlist  (barn lägger till)
  addWishlist = async (
    requestParameters: AddWishlistRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    const headerParameters: runtime.HTTPHeaders = { 'Content-Type': 'application/json' };
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/wishlist`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: runtime.toRequestBody({ shopItemId: requestParameters.shopItemId }),
      });
      return response.status;
    } catch (error) {}
  };

  // DELETE /shop/wishlist/{shopItemId}  (barn tar bort)
  removeWishlist = async (
    requestParameters: RemoveWishlistRequest,
  ): Promise<HttpStatusCode | undefined> => {
    const queryParameters: any = {};
    if (requestParameters.supervisedTag !== undefined) {
      queryParameters['supervisedTag'] = requestParameters.supervisedTag;
    }

    try {
      const response = await this.request({
        path: `/shop/wishlist/${requestParameters.shopItemId}`,
        method: 'DELETE',
        query: queryParameters,
      });
      return response.status;
    } catch (error) {}
  };
}
